import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { z } from 'zod';
import { rage } from '~/rage';
import { ClientError, ClientErrorCode, formatTime } from '~/shared';
import { Button } from '../../components/Button';
import {
	ButtonContainerWrap,
	FormContainer,
	PanelContainer,
} from '../../components/Containers';
import { FormTitle } from '../../components/FormTitle';
import { TextInput } from '../../components/TextInput';
import { useLayoutDispatch, useLayoutSelector } from '../../redux/hooks';
import {
	AuthTabs,
	setActiveTab,
	setConfirmTimer,
} from '../../redux/slices/authTabsSlice';
import {
	IFormError,
	setEmail,
	setLoading,
	setResetToken,
} from '../../redux/slices/formDataSlice';
const codeSchema = z.object({
	code: z.string().min(1, 'Введите код'),
});

const ConfirmEmail = () => {
	const loading = useLayoutSelector((state) => state.formData.loading);
	const confirmTimer = useLayoutSelector(
		(state) => state.authTabs.confirmTimer,
	);
	const emailConfirm = useLayoutSelector(
		(state) => state.authTabs.emailConfirm,
	);
	const email = useLayoutSelector((state) => state.formData.email);
	const password = useLayoutSelector((state) => state.formData.password);
	const [code, setCode] = useState('');
	const [errors, setErrors] = useState<IFormError[]>([]);
	const [timer, setTimer] = useState(0);

	const dispatch = useLayoutDispatch();

	const handleSubmit = async () => {
		dispatch(setLoading(true));

		const errors: IFormError[] = [];
		try {
			await codeSchema.parseAsync({
				code,
			});

			if (emailConfirm === 'register') {
				const result = await rage.accounts.confirmRegistrationCode(email, code);
				if (result.resetToken) {
					dispatch(setResetToken(result.resetToken));
					dispatch(setActiveTab(AuthTabs.RestoreNewPassword));
				} else {
					rage.notifications.showSuccess('Email успешно подтверждён!');
					dispatch(setActiveTab(AuthTabs.Login));
				}
			} else {
				const result = await rage.accounts.confirmResetCode(email, code);
				dispatch(setResetToken(result.token));
				dispatch(setActiveTab(AuthTabs.RestoreNewPassword));
			}
		} catch (error) {
			if (error instanceof z.ZodError) {
				error.issues.forEach((err) => {
					if (err.path && typeof err.path[0] === 'string') {
						errors.push({ source: err.path[0], text: err.message });
					}
				});
			} else if (
				error instanceof ClientError &&
				error.getCode() === ClientErrorCode.RegistrationLoginTaken
			) {
				dispatch(setActiveTab(AuthTabs.Register));
			} else {
				rage.notifications.showException(error);
			}
		}

		setErrors(errors);
		dispatch(setLoading(false));
	};

	const timeString = useMemo(
		() => formatTime(Math.max(timer * 1000, 0), '{m}:{ss}'),
		[timer],
	);

	const maskEmail = (email: string): string => {
		const [local, domain] = email.split('@');
		if (!local || !domain) return email;
		return local.slice(0, 2) + '***@' + domain;
	};

	const handleInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { value } = event.target;
			setCode(value);
			setErrors([]);
		},
		[],
	);

	const login = useLayoutSelector((state) => state.formData.login);

	const handleSendCode = useCallback(async () => {
		if (emailConfirm === 'restore') {
			try {
				const result = await rage.accounts.restorePassword(email, login);
				dispatch(setEmail(result.maskedEmail));
				setTimer(Math.floor((result.resendAvailableAt - Date.now()) / 1000));
				dispatch(setConfirmTimer(Math.floor(result.resendAvailableAt / 1000)));
			} catch (error) {
				rage.notifications.showException(error);
			}
		} else {
			try {
				const result = await rage.accounts.sendRegistrationCode(
					email,
					login,
					password,
				);
				dispatch(setEmail(result.maskedEmail));
				setTimer(Math.floor((result.resendAvailableAt - Date.now()) / 1000));
				dispatch(setConfirmTimer(Math.floor(result.resendAvailableAt / 1000)));
			} catch (error) {
				rage.notifications.showException(error);
			}
		}
	}, [emailConfirm, email, login, password]);

	const handleBack = useCallback(() => {
		if (emailConfirm === 'register') {
			dispatch(setActiveTab(AuthTabs.Register));
			return;
		}

		dispatch(setActiveTab(AuthTabs.Restore));
	}, [emailConfirm]);

	useEffectOnce(() => {
		if (confirmTimer) {
			setTimer(confirmTimer - Math.floor(Date.now() / 1000));
		}
	});

	useEffect(() => {
		if (!timer) return;
		const intervalId = setInterval(() => {
			setTimer(timer - 1);
		}, 1000);
		return () => clearInterval(intervalId);
	}, [timer]);

	return (
		<PanelContainer>
			<FormTitle
				text={
					emailConfirm === 'register'
						? 'Подтверждение кода'
						: 'Восстановление доступа к аккаунту'
				}
			/>
			<FormContainer>
				<TextInput
					name="code"
					placeholder={
						email
							? `Введите код с почты ${maskEmail(email)}`
							: 'Введите код с почты'
					}
					error={errors.find((error) => error.source === 'code')?.text}
					defaultValue={code}
					onChange={handleInputChange}
					tabIndex={1}
				/>
				<Button disabled={timer > 0} onClick={handleSendCode} code>
					{timer > 0 ? (
						<div style={{ width: 236 }}>
							<span style={{ opacity: 0.5 }}>Отправить повторно через </span>
							<span style={{ fontWeight: 500 }}>{timeString}</span>
						</div>
					) : (
						<Fragment>Отправить код повторно</Fragment>
					)}
				</Button>
			</FormContainer>
			<ButtonContainerWrap>
				<Button
					primary
					name="confirm"
					type="button"
					loading={loading}
					onClick={handleSubmit}
					tabIndex={2}
				>
					Подтвердить
				</Button>
				<Button
					name="return"
					type="button"
					onClick={handleBack}
					style={{ width: '100%', fontSize: 16 }}
					tabIndex={3}
				>
					Вернуться назад
				</Button>
			</ButtonContainerWrap>
		</PanelContainer>
	);
};

export { ConfirmEmail };
